import { initializeApp } from "firebase/app";
import { connectAuthEmulator, initializeAuth, browserLocalPersistence } from "@firebase/auth";
import { getAnalytics } from "firebase/analytics";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "@firebase/functions";
import { getMessaging, isSupported, Messaging } from "firebase/messaging";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { Capacitor } from "@capacitor/core";
import { getFirebaseConfig, getVapidKey } from "./firebaseConfig";

export const isNative = Capacitor.isNativePlatform();

/** VAPID key for Firebase Cloud Messaging push subscriptions. */
export const VAPID_KEY = getVapidKey(process.env);

const firebaseConfig = getFirebaseConfig(process.env);

export const app = initializeApp(firebaseConfig);
// Use initializeAuth with explicit persistence to avoid the WKWebView
// indexedDB auto-detection hang in Capacitor iOS.
export const auth = initializeAuth(app, {
  persistence: browserLocalPersistence,
});
export const analytics = getAnalytics(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/**
 * Firebase Messaging, lazily initialised behind the SDK's official
 * `isSupported()` check.  The promise is cached so that concurrent callers
 * (e.g. multiple `useEffect` hooks) share a single in-flight request instead
 * of racing.
 *
 * Returns `null` on browsers that lack the required APIs (WebKit, older
 * Safari, iOS Safari outside of a PWA, etc.).
 */
let _messagingPromise: Promise<Messaging | null> | null = null;
export function getMessagingSafe(): Promise<Messaging | null> {
  if (!_messagingPromise) {
    _messagingPromise = isSupported().then((supported) =>
      supported ? getMessaging(app) : null
    );
  }
  return _messagingPromise;
}
export const functionRegion = "europe-west1";
export const functions = getFunctions(app, functionRegion);

if (process.env.REACT_APP_USE_EMULATORS === "true") {
  // iOS simulator shares the host network stack so localhost works.
  // Android emulator routes to the host via the special gateway 10.0.2.2.
  const emulatorHost =
    Capacitor.getPlatform() === "android" ? "10.0.2.2" : "localhost";
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
  connectStorageEmulator(storage, emulatorHost, 9199);
}
